import React, { useEffect, useState } from 'react';
import { ClubProfile } from '../../../../utils/interfaces';

interface WedgesFieldsProps {
  clubName: string;
  club: ClubProfile;
  handleProfileChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}

const WedgesFields: React.FC<WedgesFieldsProps> = ({ clubName, club, handleProfileChange }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Check if advanced fields are filled and expand by default
  const attackAngle = (club as any)?.attack_angle;
  const launchAngle = (club as any)?.launch_angle;
  const spinRate = (club as any)?.spin_rate;

  useEffect(() => {
    const hasAdvancedValues = Boolean(
      club?.shaft ||
      club?.loft ||
      club?.bounce ||
      club?.grind ||
      attackAngle ||
      launchAngle ||
      spinRate
    );

    if (hasAdvancedValues) {
      setShowAdvanced(true);
    }
  }, [club?.shaft, club?.loft, club?.bounce, club?.grind, attackAngle, launchAngle, spinRate]);

  useEffect(() => {
    if (club && (club.loft === null || club.loft === undefined || club.loft === 0)) {
      let defaultLoft: number | null = null;
      if (clubName === 'Gap Wedge') {
        defaultLoft = 50;
      } else if (clubName === 'Sand Wedge') {
        defaultLoft = 56;
      } else if (clubName === 'Lob Wedge') {
        defaultLoft = 60;
      }

      if (defaultLoft !== null) {
        const event = {
          target: {
            name: `${clubName}.loft`,
            value: String(defaultLoft),
          },
        } as React.ChangeEvent<HTMLInputElement>;
        handleProfileChange(event);
      }
    }
  }, [club, clubName, handleProfileChange]);

  return (
    <>
      {/* Advanced section toggle */}
      <div 
        className="advanced-toggle"
        onClick={() => setShowAdvanced(!showAdvanced)}
        style={{
          cursor: 'pointer',
          padding: '12px 0',
          borderTop: '1px solid #e0e0e0',
          marginTop: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: '#666',
          fontSize: '14px',
          fontWeight: '500'
        }}
      >
        <span>Advanced Options</span>
        <span style={{ 
          transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease'
        }}>
          ▼
        </span>
      </div>

      {/* Advanced fields - conditionally rendered */}
      {showAdvanced && (
        <div style={{ marginTop: '16px' }}>
          <div className="form-field">
            <label htmlFor={`${clubName}.shaft`}>Shaft</label>
            <input
              id={`${clubName}.shaft`}
              name={`${clubName}.shaft`}
              value={club?.shaft || ''}
              onChange={handleProfileChange}
              placeholder="e.g. Ventus Blue Stiff 60g"
              maxLength={40}
            />
          </div>

          <div className="form-field">
            <label htmlFor={`${clubName}.loft`}>Loft (deg)</label>
            <input
              type="number"
              step="0.5"
              id={`${clubName}.loft`}
              name={`${clubName}.loft`}
              value={club?.loft || ''}
              onChange={handleProfileChange}
              placeholder="e.g., 56"
            />
          </div>

          <div className="form-field">
            <label htmlFor={`${clubName}.bounce`}>Bounce</label>
            <input
              type="number"
              id={`${clubName}.bounce`}
              name={`${clubName}.bounce`}
              value={club?.bounce || ''}
              onChange={handleProfileChange}
              placeholder="e.g., 10"
            />
          </div>
          
          <div className="form-field">
            <label htmlFor={`${clubName}.grind`}>Grind</label>
            <input
              id={`${clubName}.grind`}
              name={`${clubName}.grind`}
              value={club?.grind || ''}
              onChange={handleProfileChange}
              placeholder="e.g., S Grind"
              maxLength={20}
            />
          </div>

          <div className="form-field">
            <label htmlFor={`${clubName}.spin_rate`}>Spin Rate <br />(rpm)</label>
            <input
              type="number"
              id={`${clubName}.spin_rate`}
              name={`${clubName}.spin_rate`}
              value={(club as any)?.spin_rate || ''}
              onChange={handleProfileChange}
              placeholder="e.g., 2400"
            />
          </div>

          <div className="form-field">
            <label htmlFor={`${clubName}.launch_angle`}>Launch Angle <br />(deg)</label>
            <input
              type="number"
              step="0.1"
              id={`${clubName}.launch_angle`}
              name={`${clubName}.launch_angle`}
              value={(club as any)?.launch_angle || ''}
              onChange={handleProfileChange}
              placeholder="e.g., 12.5"
            />
          </div>

          <div className="form-field">
            <label htmlFor={`${clubName}.descent_angle`}>Descent <br />Angle (deg)</label>
            <input
              type="number"
              step="0.1"
              id={`${clubName}.descent_angle`}
              name={`${clubName}.descent_angle`}
              value={(club as any)?.descent_angle || ''}
              onChange={handleProfileChange}
              placeholder="e.g., 35"
            />
          </div>

          <div className="form-field">
            <label htmlFor={`${clubName}.attack_angle`}>Attack Angle <br />(deg)</label>
            <input
              type="number"
              step="0.1"
              id={`${clubName}.attack_angle`}
              name={`${clubName}.attack_angle`}
              value={(club as any)?.attack_angle || ''}
              onChange={handleProfileChange}
              placeholder="e.g., 2.5"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default WedgesFields;
